import { renderPrintDocument } from "../render/renderDocument";
import { waitForAssets } from "./waitForAssets";
import type { PrintJobSnapshot, ToHtmlOptions } from "../core/types";

async function writeIframeDocument(
  iframe: HTMLIFrameElement,
  snapshot: PrintJobSnapshot,
  mode: "preview" | "print",
  options: ToHtmlOptions = {}
) {
  const rendered = await renderPrintDocument(snapshot, { ...options, mode });

  // A blank same-origin document guarantees the iframe has a writable DOM
  // before the library injects the rendered preview or print markup.
  if (!iframe.src) {
    iframe.src = "about:blank";
  }

  const doc = iframe.contentDocument;

  if (!doc) {
    throw new Error("Unable to access iframe document.");
  }

  doc.open();
  doc.write(rendered.html);
  doc.close();

  await waitForIframeReady(iframe);
  await waitForAssets(doc);
}

function waitForIframeReady(iframe: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve) => {
    const doc = iframe.contentDocument;

    if (doc?.readyState === "complete") {
      resolve();
      return;
    }

    iframe.addEventListener("load", () => resolve(), { once: true });
    window.setTimeout(() => resolve(), 50);
  });
}

// Print always renders into a hidden iframe first so the host page styles do
// not interfere with the browser's print dialog or print media rules.
export async function printInIframe(snapshot: PrintJobSnapshot): Promise<void> {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";

  document.body.appendChild(iframe);

  try {
    await writeIframeDocument(iframe, snapshot, "print");
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  } finally {
    window.setTimeout(() => iframe.remove(), 1000);
  }
}

export { writeIframeDocument };
